import React from 'react';
import {
  Select,
  Input,
  Divider,
  RadioGroup,
  RadioButton,
  Card,
  InputNumber,
} from 'components';
import { CreateListingCardProps, CondoDetailsFields } from '../types';
import { StyledGrid } from './StyledComponents';

type CondoDetailsProps = CreateListingCardProps<CondoDetailsFields>;

type CondoType = CondoDetailsProps['condo'];

const CondoDetails: React.FC<CondoDetailsProps> = ({
  condo,
  exterior,
  garage_type,
  setListing,
}) => {
  const handleCondoChange = (changes: Partial<CondoType>) => {
    setListing(prev => ({
      ...prev,
      condo: {
        ...prev.condo,
        ...changes,
      },
    }));
  };

  const renderAllInclusive = () => {
    const getValue = () => {
      const { maintenance_option } = condo;
      if (!maintenance_option) return null;
      const values = Object.values(maintenance_option);
      const allYes = values.every(value => value === 'Yes');
      return allYes ? 'Yes' : 'No';
    };

    return (
      <RadioGroup
        label="All Inclusive"
        buttonStyle="solid"
        value={getValue()}
        onChange={({ target: { value } }) =>
          handleCondoChange({
            maintenance_option: [
              'heat',
              'hydro',
              'water',
              'cable',
              'cac',
              'building_ins',
              'parking',
            ].reduce((acc, key) => ({ ...acc, [key]: value }), {}),
          })
        }
      >
        <RadioButton value="No">No</RadioButton>
        <RadioButton value="Yes">Yes</RadioButton>
      </RadioGroup>
    );
  };

  const renderRadioGroup = (
    label: string,
    key: keyof typeof condo.maintenance_option,
    dataCy?: string,
  ) => (
      <RadioGroup
        allowClear
        label={label}
        buttonStyle="solid"
        value={condo.maintenance_option?.[key]}
        onChange={({ target: { value } }) =>
          handleCondoChange({
            maintenance_option: {
              ...condo.maintenance_option,
              [key]: value,
            },
          })
        }
        data-cy={dataCy}
      >
        <RadioButton value="No">No</RadioButton>
        <RadioButton value="Yes">Yes</RadioButton>
      </RadioGroup>
    );

  return (
    <Card type="primary" title="Condo Details" id="condoDetails">
      <StyledGrid $columns={3}>
        <Input
          label="Level #"
          value={condo.level}
          onChange={({ target: { value } }) =>
            handleCondoChange({ level: value })
          }
          data-cy="listing-condo-level"
        />
        <Input
          label="Unit #"
          value={condo.unit}
          onChange={({ target: { value } }) =>
            handleCondoChange({ unit: value })
          }
          data-cy="listing-condo-unit"
        />
        <Select
          allowClear
          label="Exposure"
          value={condo.exposure}
          onChange={value => handleCondoChange({ exposure: value })}
          options={[
            { label: 'North West', value: 'North West' },
            { label: 'North', value: 'North' },
            { label: 'North East', value: 'North East' },
            { label: 'East', value: 'East' },
            { label: 'South East', value: 'South East' },
            { label: 'South', value: 'South' },
            { label: 'South West', value: 'South West' },
            { label: 'West', value: 'West' },
          ]}
          data-cy="listing-condo-exposure"
        />
        <Input
          label="Building Name"
          value={condo.building_name}
          onChange={({ target: { value } }) =>
            handleCondoChange({ building_name: value })
          }
          data-cy="listing-condo-name"
        />
        <Input
          label="Property Management"
          value={condo.property_management}
          onChange={({ target: { value } }) =>
            handleCondoChange({ property_management: value })
          }
          data-cy="listing-property-management"
        />
        <Select
          allowClear
          label="Balcony"
          value={condo.balcony}
          onChange={value => handleCondoChange({ balcony: value })}
          options={[
            { label: 'None', value: 'None' },
            { label: 'Terr', value: 'Terr' },
            { label: 'Encl', value: 'Encl' },
            { label: 'Open', value: 'Open' },
            { label: 'Jlte', value: 'Jlte' },
          ]}
          data-cy="listing-condo-balcony"
        />
      </StyledGrid>
      <Divider />
      <StyledGrid $columns={3}>
        <Select
          allowClear
          label="Type"
          value={condo.type}
          onChange={value => handleCondoChange({ type: value })}
          options={[
            { label: 'Comm Element Condo', value: 'Comm Element Condo' },
            { label: 'Condo Apt', value: 'Condo Apt' },
            { label: 'Condo Townhouse', value: 'Condo Townhouse' },
            { label: 'Co-op Apt', value: 'Co-op Apt' },
            { label: 'Co-Ownership Apt', value: 'Co-Ownership Apt' },
            { label: 'Det Condo', value: 'Det Condo' },
            { label: 'Leasehold Condo', value: 'Leasehold Condo' },
            { label: 'Locker', value: 'Locker' },
            { label: 'Other', value: 'Other' },
            { label: 'Parking Space', value: 'Parking Space' },
            { label: 'Phased Condo', value: 'Phased Condo' },
            { label: 'Semi-Det Condo', value: 'Semi-Det Condo' },
            { label: 'Time Share', value: 'Time Share' },
            { label: 'Vacant Land Condo', value: 'Vacant Land Condo' },
          ]}
          data-cy="listing-condo-type"
        />
        <Select
          allowClear
          label="Style"
          value={condo.style}
          onChange={value => handleCondoChange({ style: value })}
          options={[
            { label: '1-Storey', value: '1-Storey' },
            { label: '2-Storey', value: '2-Storey' },
            { label: '3-Storey', value: '3-Storey' },
            { label: 'Apartment', value: 'Apartment' },
            { label: 'Bachelor/Studio', value: 'Bachelor/Studio' },
            { label: 'Bungaloft', value: 'Bungaloft' },
            { label: 'Bungalow', value: 'Bungalow' },
            { label: 'Loft', value: 'Loft' },
            { label: 'Multi-level', value: 'Multi-level' },
            { label: 'Other', value: 'Other' },
            { label: 'Stacked Townhse', value: 'Stacked Townhse' },
          ]}
          data-cy="listing-condo-style"
        />
        <Select
          allowClear
          label="Exterior"
          value={exterior}
          onChange={value => setListing(prev => ({ ...prev, exterior: value }))}
          options={[
            { label: 'Aluminium Siding', value: 'Aluminium Siding' },
            { label: 'Board & Batten', value: 'Board & Batten' },
            { label: 'Brick', value: 'Brick' },
            { label: 'Brick Front', value: 'Brick Front' },
            { label: 'Concrete', value: 'Concrete' },
            { label: 'Insulbrick', value: 'Insulbrick' },
            { label: 'Log', value: 'Log' },
            { label: 'Metal/Steel Siding', value: 'Metal/Steel Siding' },
            { label: 'Other', value: 'Other' },
            { label: 'Shingle', value: 'Shingle' },
            { label: 'Stone', value: 'Stone' },
            { label: 'Stucco (Plaster)', value: 'Stucco (Plaster)' },
            { label: 'Vinyl Siding', value: 'Vinyl Siding' },
            { label: 'Wood', value: 'Wood' },
          ]}
          data-cy="listing-condo-exterior"
        />
        <Select
          allowClear
          label="Garage Type"
          value={garage_type}
          onChange={value =>
            setListing(prev => ({ ...prev, garage_type: value }))
          }
          options={[
            { label: 'Attached', value: 'Attached' },
            { label: 'Built-in', value: 'Built-in' },
            { label: 'Carport', value: 'Carport' },
            { label: 'Detached', value: 'Detached' },
            { label: 'None', value: 'None' },
            { label: 'Other', value: 'Other' },
            { label: 'Surface', value: 'Surface' },
            { label: 'Undergrnd', value: 'Undergrnd' },
          ]}
          data-cy="listing-garage-type"
        />
        <Input
          label="Garage Spaces"
          value={condo.garage_spaces}
          onChange={({ target: { value } }) =>
            handleCondoChange({ garage_spaces: value })
          }
          data-cy="listing-condo-garage-spaces"
        />
        <Select
          allowClear
          label="Locker Type"
          value={condo.lockerType}
          onChange={value => handleCondoChange({ lockerType: value })}
          options={[
            { label: 'Owned', value: 'Owned' },
            { label: 'Exclusive', value: 'Exclusive' },
            { label: 'Common', value: 'Common' },
            { label: 'None', value: 'None' },
            { label: 'Ensuite', value: 'Ensuite' },
            { label: 'Ensuite+Common', value: 'Ensuite+Common' },
            { label: 'Ensuite+Exclusive', value: 'Ensuite+Exclusive' },
            { label: 'Ensuite+Owned', value: 'Ensuite+Owned' },
          ]}
          data-cy="listing-condo-locker-type"
        />
        <Input
          label="Parking Spaces"
          value={condo.parking_spaces}
          onChange={({ target: { value } }) =>
            handleCondoChange({ parking_spaces: value })
          }
          data-cy="listing-condo-parking-spaces"
        />
        <Input
          label="Parking Spot"
          value={condo.parking_spot}
          onChange={({ target: { value } }) =>
            handleCondoChange({ parking_spot: value })
          }
          data-cy="listing-condo-parking-spot"
        />
        <Input
          label="Locker #"
          value={condo.locker}
          onChange={({ target: { value } }) =>
            handleCondoChange({ locker: value })
          }
          data-cy="listing-condo-locker-number"
        />
      </StyledGrid>
      <Divider />
      <StyledGrid $columns={3}>
        <InputNumber
          label="Maintenance"
          type="currency"
          value={condo.maintenance}
          onChange={(value: number) =>
            handleCondoChange({
              maintenance: value ? String(value) : undefined,
            })
          }
          data-cy="listing-condo-maintenance"
        />
        <Select
          allowClear
          label="Laundry Access"
          value={condo.laundryAccess}
          onChange={value => handleCondoChange({ laundryAccess: value })}
          options={[
            { label: 'Coin', value: 'Coin' },
            { label: 'Ensuite', value: 'Ensuite' },
            { label: 'None', value: 'None' },
            { label: 'Shared', value: 'Shared' },
            { label: 'On site', value: 'On site' },
          ]}
          data-cy="listing-condo-laundry-access"
        />
        <RadioGroup
          allowClear
          label="Ensuite Laundry"
          buttonStyle="solid"
          value={condo.ensuiteLaundry}
          onChange={({ target: { value } }) =>
            handleCondoChange({ ensuiteLaundry: value })
          }
        >
          <RadioButton value="Yes" data-cy="listing-condo-ensuite-laundry-yes">
            Yes
          </RadioButton>
          <RadioButton value="No" data-cy="listing-condo-ensuite-laundry-no">
            No
          </RadioButton>
        </RadioGroup>
        <RadioGroup
          allowClear
          label="Pets Allowed"
          buttonStyle="solid"
          value={condo.pets_allowed}
          onChange={({ target: { value } }) =>
            handleCondoChange({ pets_allowed: value })
          }
        >
          <RadioButton value="Restrict" data-cy="listing-condo-pets-restricted">
            Restricted
          </RadioButton>
          <RadioButton value="N" data-cy="listing-condo-pets-no">
            No
          </RadioButton>
        </RadioGroup>
      </StyledGrid>
      <Divider />
      <StyledGrid $columns={3}>
        {renderAllInclusive()}
        {renderRadioGroup('Heat Included', 'heat')}
        {renderRadioGroup('Hydro Included', 'hydro')}
        {renderRadioGroup('Water Included', 'water')}
        {renderRadioGroup('Cable TV Included', 'cable')}
        {renderRadioGroup('CAC Included', 'cac')}
        {renderRadioGroup('Building Ins. Included', 'building_ins')}
        {renderRadioGroup(
          'Parking Included',
          'parking',
          'listing-condo-lease-parking-included',
        )}
      </StyledGrid>
    </Card>
  );
};

export default React.memo(CondoDetails);
